/**
 * Supabase client configuration
 *
 * Auth token is cached in memory via onAuthStateChange — never call
 * supabase.auth.getSession() per-request (it acquires a Navigator Lock
 * that can deadlock in Office Add-in iframes).
 */

import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabasePublishableKey) {
  throw new Error(
    `Missing Supabase environment variables:\n` +
      `VITE_SUPABASE_URL: ${supabaseUrl || "MISSING"}\n` +
      `VITE_SUPABASE_PUBLISHABLE_KEY: ${supabasePublishableKey ? "present" : "MISSING"}`
  );
}

export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Office Add-ins run in a single iframe — no cross-tab sync needed.
    // The default navigatorLock can deadlock in restricted WebView environments.
    lock: async (_name, _acquireTimeout, fn) => await fn(),
  },
});

// ---------------------------------------------------------------------------
// In-memory session cache — single onAuthStateChange listener for the app.
//
// React components: use useSyncExternalStore(subscribeSession, getSessionSnapshot)
// Non-React code:   use getAccessToken()
// ---------------------------------------------------------------------------

import type { Session } from "@supabase/supabase-js";

// undefined = INITIAL_SESSION not yet received, null = no session
let cachedSession: Session | null | undefined = undefined;
const listeners = new Set<() => void>();

supabase.auth.onAuthStateChange((_event, session) => {
  cachedSession = session;
  listeners.forEach((fn) => fn());
});

/** Subscribe to session changes (for useSyncExternalStore). */
export function subscribeSession(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** Get the current session snapshot (for useSyncExternalStore). */
export function getSessionSnapshot(): Session | null | undefined {
  return cachedSession;
}

/** Get the current access token synchronously. */
export function getAccessToken(): string | null {
  return cachedSession?.access_token ?? null;
}

/**
 * Resolve once the client has restored (or ruled out) a persisted session — i.e. INITIAL_SESSION
 * has fired and `cachedSession` is no longer `undefined`. On a hard refresh, requests that fire
 * before this point would go out with no Authorization header and pay a guaranteed
 * 401 → refresh → retry round-trip (seconds of visible loading). Bounded so a broken auth
 * pipeline can never hang every request: after the timeout callers proceed with whatever is
 * cached, falling back to the existing 401-recovery path.
 */
export function waitForSessionInitialized(timeoutMs = 5000): Promise<void> {
  if (cachedSession !== undefined) return Promise.resolve();
  return new Promise((resolve) => {
    const settle = () => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = setTimeout(settle, timeoutMs);
    const unsubscribe = subscribeSession(() => {
      if (cachedSession !== undefined) settle();
    });
  });
}
